import React from 'react'
import { Button, Icon, Spinner } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { observable, action, toJS } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Api } from 'services/Api';
import axios from 'axios';
import debounce from 'services/debounce';

const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

// How to reach the client: email and phone, each with its icon.
const ClientContact = ({ attributes }) => (
  <React.Fragment>
    {attributes.email && (
      <span className="client-suggest-contact">
        <Icon icon="envelope" iconSize={11} />
        {attributes.email}
      </span>
    )}
    {attributes.phone && (
      <span className="client-suggest-contact">
        <Icon icon="phone" iconSize={11} />
        {attributes.phone}
      </span>
    )}
  </React.Fragment>
);

const ClientRenderer = (client, { handleClick, modifiers }) => {
  const { name, email, phone } = client.attributes;
  return (
    <li key={client.id}>
      <div
        role="option"
        aria-selected={modifiers.active}
        className={`client-suggest-option${modifiers.active ? ' is-active' : ''}`}
        onClick={handleClick}
      >
        <span className="client-suggest-avatar" aria-hidden="true">{initials(name)}</span>
        <span className="client-suggest-text">
          <span className="client-suggest-name">{name}</span>
          {(email || phone) && (
            <span className="client-suggest-meta">
              <ClientContact attributes={client.attributes} />
            </span>
          )}
        </span>
      </div>
    </li>
  );
};

const ListRenderer = ({ items, itemsParentRef, query, renderItem, loading, failed }) => {
  const renderedItems = items.map(renderItem).filter(item => item != null);
  let status = null;
  if (!query) {
    status = 'Type a client name, number or email';
  } else if (failed && !loading) {
    status = 'Couldn’t load clients. Try again.';
  } else if (loading) {
    status = <span className="client-suggest-loading"><Spinner size={14} /> Searching…</span>;
  } else if (renderedItems.length === 0) {
    status = `No clients match “${query}”`;
  } else {
    status = `${renderedItems.length} ${renderedItems.length === 1 ? 'client matches' : 'clients match'} “${query}”`;
  }
  return (
    <div className="client-suggest-menu">
      <div className="client-suggest-status">{status}</div>
      {query && !loading && !failed && renderedItems.length > 0 && (
        <ul className="client-suggest-list" ref={itemsParentRef} role="listbox">
          {renderedItems}
        </ul>
      )}
    </div>
  );
}

@observer
class ClientAutocomplete extends React.Component {

  @observable loading = true;
  @observable failed = false;
  @observable results = [];
  @observable selectedClient = null;

  constructor(props) {
    super(props);
    // Create a stable debounced handler so it isn't recreated on each render
    this.debouncedOnQueryChange = debounce(this.onQueryChange.bind(this), 500);
  }

  componentDidUpdate(){
    if(this.props.value){
      this.selectedClient = this.props.value;
    }
  }

  componentDidMount() {
    let {
      selectedClientId
    } = this.props;
    if (selectedClientId) {
      let promise = Api.get(`/clients/${selectedClientId}`)
      promise.then(response => {
        this.selectedClient = response.data.data;
      })
    }
    if(this.props.value){
      this.selectedClient = this.props.value;
    }
  }

  @action
  onQueryChange(query) {
    this.failed = false;
    this.loading = true;
    if (query === '') {
      return;
    }
    let promise = Api.post('/clients/search', { search: { term: query, per_page: 50, page: 1, order: { name: 'asc' } } })
    promise
      .then(this.onClientsLoad.bind(this))
      .catch(this.onClientsLoadFail.bind(this))
    return promise;
  }

  @action
  onClientsLoadFail(error) {
    if (axios.isCancel(error)) {
      this.loading = true;
    } else {
      this.loading = false;
      this.failed = true;
    }
  }

  @action
  onClientsLoad(response) {
    this.loading = false;
    this.failed = false;
    this.results = response.data.data;
  }

  @action
  onClientSelect(client) {
    this.selectedClient = client;
    this.props.onClientSelect(client);
  }

  @action
  clearSelectedClient() {
    this.selectedClient = null;
    this.props.onClientSelect(null);
  }

  // The chosen client as one field: name, how to reach them, a link to their
  // page and a button to pick someone else.
  renderPicked(client) {
    const { name, description } = client.attributes;
    return (
      <div className="client-picked">
        <span className="client-suggest-avatar" aria-hidden="true">{initials(name)}</span>
        <span className="client-picked__text">
          <span className="client-picked__name" title={name}>{name}</span>
          <span className="client-picked__meta">
            <ClientContact attributes={client.attributes} />
          </span>
          {description && <span className="client-picked__note" title={description}>{description}</span>}
        </span>
        {client.id && (
          <a
            className="client-picked__action"
            href={`/dashboard/clients/${client.id}?resource=jobs`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open the client in a new tab"
            aria-label={`Open ${name} in a new tab`}
          >
            <Icon icon="share" iconSize={14} />
          </a>
        )}
        <button
          type="button"
          className="client-picked__action"
          onClick={() => this.clearSelectedClient()}
          title="Choose a different client"
          aria-label="Choose a different client"
        >
          <Icon icon="cross" iconSize={14} />
        </button>
      </div>
    );
  }

  render() {
    let {
      failed,
      results,
      onQueryChange,
      loading,
      onClientSelect,
      selectedClient,
      clearSelectedClient,
      props: {
        inputProps
      }
    } = this;
    if (this.props.showDetails && selectedClient && typeof selectedClient !== 'string') {
      return this.renderPicked(selectedClient);
    }
    return(
      <div className="client-name-field">
        {
          selectedClient ? <Button
              ellipsize={true}
              title={
                typeof selectedClient === 'string'
                  ? selectedClient
                  : selectedClient.attributes.name
              }
              text={
                typeof selectedClient === 'string'
                  ? selectedClient
                  : selectedClient.attributes.name
              }
              rightIcon='cross'
              onClick={clearSelectedClient.bind(this)}
            /> : <Suggest
              inputProps={{
                icon: 'cross',
                placeholder: 'Search client...',
                ...inputProps
              }}
              closeOnSelect={true}
              openOnKeyDown={true}
              resetOnQuery={true}
              resetOnSelect={true}
              popoverProps={{ minimal: true }}
              onQueryChange={this.debouncedOnQueryChange}
              items={toJS(results)}
              itemRenderer={ClientRenderer}
              itemListRenderer={(props) => <ListRenderer {...props} loading={loading} failed={failed} />}
              inputValueRenderer={(client) => client.attributes.name }
              onItemSelect={onClientSelect.bind(this)}
            />
        }
      </div>
    )
  }
}

ClientAutocomplete.propTypes = {
  inputProps: PropTypes.object,
  // Show the chosen client with their email and phone, instead of the name alone.
  showDetails: PropTypes.bool,
  onClientSelect: PropTypes.func.isRequired
}

export default ClientAutocomplete;